import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Loader2, MoveLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button, buttonVariants } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import useAuthStore from 'store/auth';
import {
  ForgotPasswordFormData,
  ForgotPasswordValidator,
} from 'lib/validators/forgot-password';
import { useSignUp } from 'services/auth/hook';
import { SignUpFlowType } from 'services/auth/type';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setForgotEmail = useAuthStore((store) => store.setForgotEmail);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setForgotEmail('');
  }, [setForgotEmail]);

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(ForgotPasswordValidator),
    defaultValues: {
      email: '',
    },
  });

  const { mutate } = useSignUp({
    onSuccess: (_, { data: { username } }) => {
      setForgotEmail(username);
      navigate(PATH_ROUTES.FORGOT_PASSWORD_OTP);
    },
    onSettled: () => setIsLoading(false),
  });

  function onSubmit(values: ForgotPasswordFormData) {
    setIsLoading(true);
    mutate({
      flowType: SignUpFlowType.FORGOT_PASSWORD,
      data: { username: values.email },
    });
  }

  return (
    <>
      <div className='absolute hidden lg:block top-4 left-4'>
        <Link
          to={PATH_ROUTES.LOGIN}
          className={buttonVariants({
            variant: 'ghost',
            className: 'rounded-md !px-4',
            size: 'sm',
          })}
        >
          <MoveLeft className='w-5 h-5' />
        </Link>
      </div>

      <div className='flex flex-col items-center max-w-md mx-auto space-y-8 lg:max-w-sm'>
        <div className='flex flex-col items-center justify-center px-6 space-y-2'>
          <h2 className='text-2xl font-semibold leading-none tracking-tight text-center transition-colors text-primary'>
            {t('forgot_password')}
          </h2>
          <p className='font-medium leading-none text-center text-primary'>
            {t('reset_password_message')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='w-full px-8 space-y-8 sm:px-0 sm:w-3/4 lg:w-2/3'
          >
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('email')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-center'>
              <Button
                type='submit'
                className='px-6 font-semibold rounded-full'
                disabled={!form.formState.isValid || isLoading}
              >
                {isLoading && (
                  <Loader2
                    role='progressbar'
                    className='w-4 h-4 mr-2 animate-spin'
                  />
                )}{' '}
                {t('send_verfication_code')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
